import { AllCardsType } from '../../../data/all-cards'
import { NombreTarotista } from '../../../data/personality'
import { DataTypePregunta } from '../../../interfaces/utils'
import { buildComboPreamble, ComboPreviousStep } from '../../../utils/combo-context'
import { formatCardLabel } from '../../../utils/card-label'
import {
    ABUNDANCIA_CONSCIENTE_NAME,
    ABUNDANCIA_CONSCIENTE_PREAMBLE,
    ABUNDANCIA_CONSCIENTE_STEPS,
    ABUNDANCIA_CONSCIENTE_TOTAL_STEPS,
} from './combo-config'

const cardLabel = (card: AllCardsType) => formatCardLabel(card)

const consultaExtra = (data: DataTypePregunta) =>
    data.consulta.length > 0
        ? 'ten en cuenta en la tirada la siguiente consulta que realiza el consultante: ' +
          data.consulta +
          ','
        : ''

const buildStepPrompt = (step: number, cards: AllCardsType[], data: DataTypePregunta): string => {
    const extra = consultaExtra(data)

    if (step === 1) {
        return `Quiero que interpretes una **Tirada de Abundancia y Prosperidad** con 10 cartas, analizando el estado financiero y energético del consultante ${
            data.consultante
        }, ${extra} según estas cartas: **Energía actual respecto a la abundancia:** ${cardLabel(
            cards[0]
        )}, **Bloqueo o creencia limitante:** ${cardLabel(cards[1])}, **Origen del bloqueo:** ${cardLabel(
            cards[2]
        )}, **Recursos ocultos o talentos desaprovechados:** ${cardLabel(cards[3])}, **Oportunidades en camino:** ${cardLabel(
            cards[4]
        )}, **Acción clave para atraer prosperidad:** ${cardLabel(cards[5])}, **Cómo gestionar mejor sus finanzas:** ${cardLabel(
            cards[6]
        )}, **Consejo del universo:** ${cardLabel(cards[7])}, **Posibles obstáculos futuros:** ${cardLabel(
            cards[8]
        )} y **Resultado final si se sigue el consejo:** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado dentro de la tirada. Luego, brinda consejos específicos para atraer abundancia y prosperidad en la vida del consultante basados en la interpretación. Mantén un tono profesional y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la interpretación, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    if (step === 2) {
        return `Quiero que interpretes una **Tirada de Liberación de Bloqueo del Dinero** con 10 cartas, ayudando al consultante ${
            data.consultante
        } a identificar y liberar bloqueos financieros, creencias limitantes y patrones que frenan su abundancia, ${extra} según estas cartas: **Tu relación actual con el dinero (¿cómo es tu energía real con el dinero hoy?):** ${cardLabel(
            cards[0]
        )}, **Creencia limitante principal (¿cuál es la creencia que más te está frenando?):** ${cardLabel(
            cards[1]
        )}, **Origen del bloqueo (¿de dónde viene esa creencia o patrón?):** ${cardLabel(
            cards[2]
        )}, **Cómo se manifiesta en tu vida (¿de qué forma concreta te está afectando?):** ${cardLabel(
            cards[3]
        )}, **Beneficio secundario del bloqueo (¿qué te "da" mantener este bloqueo, aunque duela?):** ${cardLabel(
            cards[4]
        )}, **Miedo o resistencia que hay detrás (¿qué miedo hay debajo de este bloqueo?):** ${cardLabel(
            cards[5]
        )}, **Qué tenés que soltar (¿qué creencia o patrón necesitás liberar?):** ${cardLabel(
            cards[6]
        )}, **Nueva creencia o energía a incorporar (¿qué creencia o energía necesitás integrar?):** ${cardLabel(
            cards[7]
        )}, **Acción concreta recomendada (¿qué paso podés dar para empezar a mover esto?):** ${cardLabel(
            cards[8]
        )} y **Energía de abundancia que viene (¿qué energía de dinero se está abriendo si hacés este trabajo?):** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente sobre el bloqueo del dinero: conecta la relación actual con el dinero, la creencia limitante, su origen, cómo se manifiesta, el beneficio secundario del bloqueo y el camino de liberación. Detecta patrones, miedos ocultos y resistencias. Proporciona consejos prácticos y concretos para transformar la relación con el dinero y abrirse a la abundancia. Mantén un tono profesional, empático, motivador y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    return `Quiero que interpretes una **Tirada de Conexión con tu Yo Futuro** con 10 cartas, ayudando al consultante ${
        data.consultante
    } a conectar con la versión de sí mismo que puede llegar a ser en los próximos 1 a 3 años en el ámbito de la abundancia y prosperidad, ${extra} según estas cartas: **Energía actual del consultante:** ${cardLabel(
        cards[0]
    )}, **Lo que más está pidiendo atención ahora:** ${cardLabel(cards[1])}, **Energía y presencia del Yo Futuro (quién se convierte esta persona dentro de 1 a 3 años; enfócate en su identidad, energía, presencia y cualidades principales):** ${cardLabel(
        cards[2]
    )}, **Cómo se siente emocionalmente esa versión futura:** ${cardLabel(cards[3])}, **Qué está creando o viviendo esa versión futura:** ${cardLabel(
        cards[4]
    )}, **Qué necesita soltar para llegar ahí:** ${cardLabel(cards[5])}, **Qué necesita cultivar o activar:** ${cardLabel(
        cards[6]
    )}, **Apoyo invisible o guías que ya tiene disponible:** ${cardLabel(cards[7])}, **El paso más poderoso y realista que puede dar ahora:** ${cardLabel(
        cards[8]
    )} y **Mensaje final de su Yo Futuro + visión del camino si sigue esta línea:** ${cardLabel(
        cards[9]
    )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Conecta el presente con el yo futuro en clave de abundancia: muestra cómo la energía actual, lo que pide atención y lo que debe soltar o cultivar se relacionan con la identidad próspera que revela el yo futuro. Detecta patrones, bloqueos y oportunidades en la lectura. Proporciona consejos prácticos, realistas y motivadores para acercarse a esa versión futura. Mantén un tono profesional, empático, esperanzador y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
}

export const interpretStep = (
    step: number,
    cards: AllCardsType[],
    data: DataTypePregunta,
    user: string,
    personality: NombreTarotista,
    previousSteps: ComboPreviousStep[],
    comboId: string
) => {
    const config = ABUNDANCIA_CONSCIENTE_STEPS[step - 1]
    const preamble = buildComboPreamble(
        ABUNDANCIA_CONSCIENTE_NAME,
        step,
        ABUNDANCIA_CONSCIENTE_TOTAL_STEPS,
        previousSteps,
        ABUNDANCIA_CONSCIENTE_PREAMBLE
    )
    const consult = preamble + buildStepPrompt(step, cards, data)

    return {
        consult,
        user,
        personality,
        clientName: data.consultante.name,
        clientPhone: data.consultante.phone || '',
        clientAnswer: data.consulta,
        cards,
        type: config.type,
        comboId,
        comboName: ABUNDANCIA_CONSCIENTE_NAME,
        comboStep: step,
        comboTotalSteps: ABUNDANCIA_CONSCIENTE_TOTAL_STEPS,
    }
}
